import { useIntl } from 'react-intl';
import { useTitleStore } from 'src/stores/useTitleStore';
import React from 'react';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { useListZoneVisits } from 'src/features/statistic/api/useListZoneVisits';
import { useSearchParams } from 'react-router-dom';
import { CellDatetime, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import { ZoneVisitsStatistics } from 'src/features/statistic/types';
import { createColumnHelper } from '@tanstack/react-table';
import DataTableColumnHeader from '../../../registry/components/table/DataTableColumnHeader';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import usePagination from 'src/registry/hooks/usePagination';
import { useParking } from 'src/providers/ParkingProvider';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { membershipMessages } from 'src/features/membership/utils/messages';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { siteMessages } from 'src/features/sites/utils/messages';

const CapacityStatsTable = () => {
    const { formatMessage, locale } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const { parkingId } = useParking();
    const { queryPaginationOptions, ...pagination } = usePagination();

    const [searchParams] = useSearchParams();

    const active_as_of_after = searchParams.get('active_as_of_after');
    const active_as_of_before = searchParams.get('active_as_of_before');
    const zone = searchParams.get('zone');

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const accessMemberships = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);
    const accessMembershipTemplate = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);

    React.useEffect(() => {
        setTitle(formatMessage(statisticMessages.capacityOverview));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data, isLoading } = useListZoneVisits({
        parkingId,
        active_as_of_after: active_as_of_after ?? undefined,
        active_as_of_before: active_as_of_before ?? undefined,
        zone: zone ?? undefined,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<ZoneVisitsStatistics>();
    const columns = [
        columnHelper.accessor('start_datetime', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            meta: {
                title: formatMessage(visitorMessages.enteredAt),
            },
        }),
        columnHelper.accessor('end_datetime', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            meta: {
                title: formatMessage(visitorMessages.exitAt),
            },
        }),
        columnHelper.accessor('visitor.main_identifier.value', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        toPath={`/${PATH.VISITORS}/visitor/${ctx.row.original.visitor?.id}`}
                        linkTitle={ctx.row.original.visitor.main_identifier?.value ?? '-'}
                        hasPermissions={accessVisitors}
                    />
                );
            },
            meta: {
                title: formatMessage(visitorMessages.visitor),
            },
        }),
        columnHelper.accessor('site', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                const site = ctx.row.original.site;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        toPath={`/${PATH.ADMINISTRATION}/site/${site.id}`}
                        linkTitle={
                            site?.translations.find((translation) => translation.language === locale)?.name || '-'
                        }
                        hasPermissions={accessSites}
                    />
                );
            },
            meta: {
                title: formatMessage(siteMessages.site),
            },
        }),
        columnHelper.accessor('membership.name', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const membershipName = row.original?.membership?.name;
                const membershipId = row.original?.membership?.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.MEMBERSHIPS}/all/${membershipId}`}
                        linkTitle={membershipName}
                        hasPermissions={accessMemberships}
                    />
                );
            },
            meta: {
                title: formatMessage(membershipMessages.membership),
            },
        }),
        columnHelper.accessor('membership_type', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const templateName = row.original?.membership_type?.translations?.find(
                    (temp) => temp.language === locale,
                )?.name;
                const templateId = row.original?.membership_type?.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/membership-templates/${templateId}`}
                        linkTitle={templateName}
                        hasPermissions={accessMembershipTemplate}
                    />
                );
            },
            meta: {
                title: formatMessage(membershipMessages.template),
            },
        }),
    ];

    return (
        <DataTable<ZoneVisitsStatistics, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            pagination={pagination}
            semantics={{
                title: formatMessage(statisticMessages.allVisits),
                entryName: formatMessage(visitorMessages.visit).toLowerCase(),
                entryNames: formatMessage(visitorMessages.visits).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default CapacityStatsTable;
